import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Select, Button, Card, Space, Typography, Divider, Row, Col, Tooltip, Popover, List, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, InsertRowBelowOutlined, ClockCircleOutlined } from '@ant-design/icons';
import globalVariableService from '../../../services/GlobalVariableService';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

// Custom Input component that shows a list of columns on click/focus and inserts selected column at cursor
const FormulaInput = ({ value = '', onChange, options = [] }) => {
  const [open, setOpen] = useState(false);
  const [filterToken, setFilterToken] = useState('');
  const inputRef = useRef(null);

  // Helper to find the word at cursor
  const getWordAtCursor = (text, position) => {
    const left = text.slice(0, position);
    // Match word characters (letters, numbers, underscore) at the end of the string
    const match = left.match(/[a-zA-Z0-9_]+$/);
    return match ? { word: match[0], start: match.index } : { word: '', start: position };
  };

  const updateFilter = (target) => {
    const pos = target.selectionStart;
    const val = target.value;
    const { word } = getWordAtCursor(val, pos);
    setFilterToken(word);
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    updateFilter(e.target);
    if (!open) setOpen(true);
  };

  const handleClick = (e) => {
    updateFilter(e.target);
    // If already open, stop propagation to prevent toggling off
    if (open) {
      e.stopPropagation();
    }
  };

  const handleKeyUp = (e) => {
    updateFilter(e.target);
  };

  const handleInsert = (text) => {
    // Support both Input and TextArea refs
    const input = inputRef.current?.resizableTextArea?.textArea || inputRef.current?.input;
    
    // Auto-quote if column name has spaces or special chars
    const textToInsert = /[^a-zA-Z0-9_]/.test(text) ? `"${text}"` : text;

    if (input) {
      const currentPos = input.selectionStart || 0;
      const { word, start } = getWordAtCursor(value, currentPos);
      
      const val = value || '';
      // Replace the current word (filter token) with the selected column
      const newValue = val.substring(0, start) + textToInsert + val.substring(currentPos);
      onChange(newValue);

      // Restore focus and move cursor after inserted text
      setTimeout(() => {
        input.focus();
        const newPos = start + textToInsert.length;
        input.setSelectionRange(newPos, newPos);
        setFilterToken(''); // Reset filter after insertion
      }, 0);
    } else {
      onChange((value || '') + textToInsert);
    }
    setOpen(false);
  };

  const filteredOptions = options.filter(opt => 
    !filterToken || opt.value.toLowerCase().includes(filterToken.toLowerCase())
  );

  // Get global variables from service
  const globalVariables = globalVariableService.getAllVariables();
  const variableEntries = Object.entries(globalVariables);
  
  // Filter variables based on filterToken
  const filteredVariables = variableEntries.filter(([name]) => 
    !filterToken || name.toLowerCase().includes(filterToken.toLowerCase())
  );

  const handleInsertVariable = (varName) => {
    const input = inputRef.current?.resizableTextArea?.textArea || inputRef.current?.input;
    const textToInsert = `{{${varName}}}`;
    
    if (input) {
      const currentPos = input.selectionStart || 0;
      const { start } = getWordAtCursor(value, currentPos);
      const val = value || '';
      const newValue = val.substring(0, start) + textToInsert + val.substring(currentPos);
      onChange(newValue);
      setTimeout(() => {
        input.focus();
        const newPos = start + textToInsert.length;
        input.setSelectionRange(newPos, newPos);
        setFilterToken('');
      }, 0);
    } else {
      onChange((value || '') + textToInsert);
    }
    setOpen(false);
  };

  const content = (
    <div style={{ width: 300, maxHeight: 350, overflowY: 'auto' }}>
      {/* Columns Section */}
      <div style={{ padding: '4px 8px', background: '#f5f5f5', borderBottom: '1px solid #e8e8e8', fontWeight: 500 }}>
        <Text strong>Columns</Text> {filterToken && <Text type="secondary" style={{fontSize: 11}}>(filtering by "{filterToken}")</Text>}
      </div>
      <List
        dataSource={filteredOptions}
        style={{ maxHeight: 120, overflowY: 'auto' }}
        renderItem={item => (
          <List.Item
            onClick={() => handleInsert(item.value)}
            style={{ cursor: 'pointer', padding: '6px 12px', transition: 'background 0.3s' }}
            className="formula-list-item"
            onMouseEnter={(e) => e.currentTarget.style.background = '#e6f7ff'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Text code>{item.value}</Text>
          </List.Item>
        )}
        locale={{ emptyText: <Text type="secondary" style={{ fontSize: 11 }}>No matching columns</Text> }}
      />
      
      {/* Global Variables Section */}
      <div style={{ padding: '4px 8px', background: '#f6ffed', borderBottom: '1px solid #b7eb8f', borderTop: '1px solid #e8e8e8', fontWeight: 500 }}>
        <Text strong style={{ color: '#52c41a' }}><ClockCircleOutlined /> Variables</Text>
      </div>
      {filteredVariables.length > 0 ? (
        <List
          dataSource={filteredVariables}
          style={{ maxHeight: 150, overflowY: 'auto' }}
          renderItem={([name, varData]) => (
            <List.Item
              onClick={() => handleInsertVariable(name)}
              style={{ cursor: 'pointer', padding: '6px 12px', transition: 'background 0.3s' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f6ffed'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Space>
                <Tag color="green">{`{{${name}}}`}</Tag>
                <Text type="secondary" style={{ fontSize: 10 }}>{typeof varData === 'object' ? varData.value : varData}</Text>
              </Space>
            </List.Item>
          )}
        />
      ) : (
        <div style={{ padding: '8px 12px', color: '#999', fontSize: 11 }}>No variables defined</div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      overlayClassName="formula-popover"
    >
      <div style={{ position: 'relative' }}>
        <Input.TextArea
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onClick={handleClick}
            onKeyUp={handleKeyUp}
            placeholder="e.g. price * 1.1"
            autoSize={{ minRows: 1, maxRows: 6 }}
            style={{ paddingRight: 30, fontFamily: 'monospace' }}
        />
        <Tooltip title="Click to insert column or variable">
            <InsertRowBelowOutlined 
                style={{ 
                    position: 'absolute', 
                    right: 8, 
                    top: 8, 
                    color: '#bfbfbf', 
                    cursor: 'pointer',
                    zIndex: 1
                }} 
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                }} 
            />
        </Tooltip>
      </div>
    </Popover>
  );
};

const AddColumnNodeProperties = ({ config, form, inputSchema, updateNodeName, updateConfig }) => {
  const [columns, setColumns] = useState(config.columns || []);
  const prevColumnsRef = React.useRef(columns);

  // Sync local state with config when config changes externally (e.g. undo/redo)
  useEffect(() => {
    if (config.columns && JSON.stringify(config.columns) !== JSON.stringify(columns)) {
      setColumns(config.columns);
      prevColumnsRef.current = config.columns;
    }
  }, [config.columns]);

  // Handle updates when local columns state changes
  useEffect(() => {
    // Only proceed if columns have actually changed
    if (JSON.stringify(prevColumnsRef.current) === JSON.stringify(columns)) {
      return;
    }

    // Update form
    form.setFieldsValue({
      columns: columns
    });
    
    // Auto-update node name
    if (updateNodeName) {
      const validColumns = columns.filter(c => c.name && c.name.trim() !== '');
      if (validColumns.length > 0) {
        const names = validColumns.map(c => c.name).join(', ');
        // Truncate if too long
        const displayName = names.length > 30 ? names.substring(0, 27) + '...' : names;
        updateNodeName(`Add: ${displayName}`);
      } else {
        updateNodeName('Add Column');
      }
    }

    // Save columns to config
    if (updateConfig) {
        updateConfig({
            config: {
                ...config,
                columns: columns
            }
        });
    }

    // Update ref
    prevColumnsRef.current = columns;
  }, [columns, form, updateNodeName, updateConfig, config]);

  // Add new column
  const addColumn = () => {
    const newColumn = {
      id: `col_${Date.now()}`,
      name: `new_column_${columns.length + 1}`,
      formula: '',
      dataType: 'string',
      position: 'end',
      relativeTo: null
    };
    setColumns([...columns, newColumn]);
  };

  // Remove column
  const removeColumn = (index) => {
    const newColumns = columns.filter((_, i) => i !== index);
    setColumns(newColumns);
  };

  // Update column
  const updateColumn = (index, updates) => {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], ...updates };
    setColumns(newColumns);
  };

  return (
    <div>
      <Form.Item label="Description" name="description" initialValue={config.description || 'Add calculated columns to dataset'}>
        <TextArea rows={2} placeholder="Describe what columns will be added" />
      </Form.Item>

      <Divider orientation="left">Column Definitions</Divider>
      
      {/* Hidden form field to store columns in form */}
      <Form.Item name="columns" initialValue={columns} style={{ display: 'none' }}>
        <Input />
      </Form.Item>

      {/* Columns List */}
      <div style={{ marginBottom: 16 }}>
        {columns.map((column, index) => (
          <Card 
            key={column.id}
            size="small" 
            style={{ marginBottom: 12 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{column.name || `Column ${index + 1}`}</Text>
                <Button 
                  type="text" 
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeColumn(index)}
                />
              </div>
            }
          >
            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Column Name</Text>
                  <Input 
                    value={column.name}
                    onChange={(e) => updateColumn(index, { name: e.target.value })}
                    placeholder="new_column"
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Data Type</Text>
                  <Select 
                    style={{ width: '100%' }}
                    value={column.dataType}
                    onChange={(value) => updateColumn(index, { dataType: value })}
                  >
                    <Option value="string">Text</Option>
                    <Option value="number">Number</Option>
                    <Option value="date">Date</Option>
                    <Option value="boolean">Boolean</Option>
                  </Select>
                </div>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <div style={{ marginBottom: 8 }}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Position</Text>
                  <Select 
                    style={{ width: '100%' }}
                    value={column.position || 'end'}
                    onChange={(value) => updateColumn(index, { position: value })}
                  >
                    <Option value="end">At the end</Option>
                    <Option value="start">At the beginning</Option>
                    <Option value="after">After column...</Option>
                  </Select>
                </div>
              </Col>
              {column.position === 'after' && (
                  <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                      <Text style={{ fontSize: '12px', color: '#666' }}>Relative To</Text>
                      <Select 
                        style={{ width: '100%' }}
                        value={column.relativeTo}
                        onChange={(value) => updateColumn(index, { relativeTo: value })}
                        placeholder="Select column"
                        showSearch
                        notFoundContent="No columns available"
                      >
                        {inputSchema && inputSchema.length > 0 ? (
                            inputSchema.map(col => (
                                <Option key={col.name} value={col.name}>{col.name}</Option>
                            ))
                        ) : (
                            <Option disabled value="no_columns">No input columns found</Option>
                        )}
                      </Select>
                      {(!inputSchema || inputSchema.length === 0) && (
                          <Text type="secondary" style={{ fontSize: '10px', color: '#faad14' }}>
                              Connect to a configured source node to see columns.
                          </Text>
                      )}
                    </div>
                  </Col>
              )}
            </Row>

            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: '12px', color: '#666' }}>Formula</Text>
              <FormulaInput 
                value={column.formula}
                onChange={(val) => updateColumn(index, { formula: val })}
                options={inputSchema?.map(col => ({ value: col.name, label: col.name })) || []}
              />
            </div>
          </Card>
        ))}
      </div>

      {/* Add New Column Button */}
      <Button 
        type="dashed" 
        onClick={addColumn}
        block
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
      >
        Add New Column
      </Button>

      <Divider orientation="left" style={{ fontSize: '12px' }}>SQL Syntax Guide</Divider>
      
      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '11px' }}>
        <Text type="secondary">
          <strong>Supported SQL Syntax:</strong><br/>
          • Columns: <code>column_name</code><br/>
          • Math: <code>+, -, *, /, %</code><br/>
          • Comparison: <code>{`>, <, >=, <=, =, !=, <>`}</code><br/>
          • Logic: <code>AND, OR, NOT, CASE WHEN ... THEN ... END</code><br/>
          • Functions: <code>ABS(), ROUND(), UPPER(), LOWER(), COALESCE()</code><br/>
          • Strings: <code>'text'</code> (single quotes), <code>||</code> (concat)<br/>
          • Casting: <code>CAST(col AS INT)</code>, <code>CAST(col AS VARCHAR)</code>
        </Text>
      </div>
    </div>
  );
};

export default AddColumnNodeProperties;